import React, { useRef } from 'react';
import { Box, Typography, makeStyles, Tooltip } from '@material-ui/core';
import AppDialog from 'components/Dialog/AppDialog';
import AppButton from 'components/CustomButtons/AppButton';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { showToast } from 'store/slices/toast';
import { Formik, Form, Field } from 'formik';
import SelectField from 'components/Select/SelectField';
import { FILE_STATUS } from 'config/AppConfig';
import AppColor from "utilities/AppColor";
import { maxFileSize } from 'config/AppConfig';

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/jpg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const useStyles = makeStyles(() => ({
  sectionTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0D9488",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "10px 0 15px",
    display: "block",
  },
  control: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginBottom: 10,
    gap: 8,
  },
  fileBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 6,
    marginBottom: "1rem",
  },
  fileName: {
    fontSize: 13,
    color: "#374151",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

/**
 * StaffUploadDocumentModal
 *
 * Manages its own local state (localFiles, deletedFiles).
 * Only pushes data to parent via onConfirm(file, expiryDate, fileType, status, finalDeletedIds).
 * Cancel / close discards all local changes — parent state is untouched.
 *
 * Props:
 *   initialDeletedIds  – ids[] of server files already confirmed-deleted for this documentType
 *   onConfirm(file, expiryDate, fileType, status, finalDeletedIds)
 */
const StaffUploadDocumentModal = ({
  open,
  fileTypeLabel,
  initialFile,
  initialExpiry,
  initialStatus,
  initialDeletedIds = [],
  item,
  items = [],
  fileNameKey,
  fileLinkKey,
  isAdmin,
  generalName,
  fileNameSelected,
  onClose,
  onConfirm,
  categories = [],
  needsExpiry,
  autoExpiry,
  isMultiple = false,
  documentsType,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const hiddenFileInputRef = useRef(null);
  const isShowListFile = !!items?.length;
  console.log(' >>>> items: ', items);
  
  // Internal state — never touches parent until confirm
  const [localFiles, setLocalFiles] = React.useState([]);
  const [deletedIds, setDeletedIds] = React.useState([]); // ids[] for this documentType only

  // Sync localFiles from initialFile whenever the modal is (re)mounted or initialFile changes
  React.useEffect(() => {
    if (initialFile) {
      const filesArray = Array.isArray(initialFile) ? initialFile : [initialFile];
      setLocalFiles(filesArray.filter(f => f instanceof File));
    } else {
      setLocalFiles([]);
    }
  }, [initialFile]);

  // Sync deletedIds from initialDeletedIds on mount (modal remounts per editing context)
  React.useEffect(() => {
    setDeletedIds(initialDeletedIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── File input handlers ──────────────────────────────────────────────────

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const invalidFile = files.find(f => !ALLOWED_FILE_TYPES.includes(f.type));
    if (invalidFile) {
      dispatch(showToast({ type: 'error', message: t('validation.invalidFileImage') || 'Only PDF, Word, or Image files are allowed' }));
      return;
    }

    const oversizedFile = files.find(f => f.size > maxFileSize);
    if (oversizedFile) {
      dispatch(showToast({ type: 'warning', message: t('validation.invalidSize', { maxSize: '2MB' })}));
      return;
    }

    if (isMultiple) {
      setLocalFiles(prev => {
        const existingKeys = new Set(prev.map(f => `${f.name}-${f.size}`));
        const newFiles = files.filter(f => !existingKeys.has(`${f.name}-${f.size}`));
        return [...newFiles, ...prev];
      });
    } else {
      setLocalFiles([files[0]]);
    }
  };

  const handleRemoveLocalFile = (index) => {
    setLocalFiles(prev => prev.filter((_, i) => i !== index));
  };

  // ─── Deleted-server-file handlers ────────────────────────────────────────

  const handleDeleteServerFile = (id) => {
    setDeletedIds(prev => prev.includes(id) ? prev : [...prev, id]);
  };

  const handleUndeleteServerFile = (id) => {
    setDeletedIds(prev => prev.filter(existingId => existingId !== id));
  };

  // ─── Confirm ─────────────────────────────────────────────────────────────

  const handleConfirm = (values) => {
    const filesToConfirm = isMultiple
      ? localFiles
      : (localFiles.length > 0 ? localFiles[0] : null);

    // Pass finalDeletedIds so parent can replace (not merge) for this documentType
    onConfirm(
      filesToConfirm,
      values.expiryDate,
      values.fileType,
      localFiles.length > 0 ? null : values.status,
      deletedIds,
    );
  };

  // ─── Display helpers ──────────────────────────────────────────────────────

  const fileName = isMultiple
    ? (localFiles.length > 0
      ? (localFiles.length === 1 ? localFiles[0].name : `${localFiles.length} files selected`)
      : fileNameSelected)
    : (localFiles.length > 0 ? localFiles[0].name : fileNameSelected);

  const getUploadMessage = () => {
    let totalFiles = (items?.length || 0) + (localFiles.length || 0);

    if (totalFiles === 0) {
      return (generalName || fileName || <span style={{ color: "red" }}>{t('label.noFileSelected')}</span>);
    }
    if (isMultiple || isShowListFile) {
      return `${items?.length} ${items?.length === 1 ? t("label.fileAttached") : t("label.filesAttached")}${localFiles.length > 0 ? ` (${localFiles.length} ${t("label.new")})` : ''}`;
    } else {
      return fileName
    }
  };

  const isShowExpiry = (values) => {
    if (categories.length > 0) {
      const selectedCat = categories.find(c => c.key === values.fileType);
      return !!(selectedCat && selectedCat.needsExpiry && !selectedCat.autoExpiry);
    }
    if (autoExpiry) return false;
    if (needsExpiry !== undefined) return needsExpiry;
    if (fileTypeLabel) return true;
    return false;
  };

  const uploadMessage = getUploadMessage();
  const labelUploadBtn = (item?.id && !isShowListFile) || (localFiles.length === 1 && !isMultiple) ? t('button.change') : t('button.upload');

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <AppDialog
      open={open}
      title={item?.id ? t('title.editDocument') : t('title.uploadDocument')}
      onClose={onClose}
      maxWidth="sm"
    >
      <Box>
        <Formik
          enableReinitialize
          initialValues={{
            expiryDate: initialExpiry || '',
            fileType: '',
            status: initialStatus || '',
          }}
          validate={(values) => {
            const errors = {};
            if (categories.length > 0 && !values.fileType) {
              errors.fileType = t('validation.required');
            }
            if (isShowExpiry(values) && !values.expiryDate) {
              errors.expiryDate = t('validation.required');
            }
            return errors;
          }}
          onSubmit={handleConfirm}
        >
          {({ values }) => {
            const showExpiry = isShowExpiry(values);
            const computedShowStatus = isAdmin && !!item?.id && localFiles.length === 0;

            return (
              <Form>
                <Typography className={classes.sectionTitle}>
                  {t('clientDocument.sectionDocument')}
                </Typography>

                <Box className={classes.fileBox} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <i className="fas fa-file-pdf" style={{ fontSize: 18, color: "#EF4444" }} />
                    <Box className={classes.fileName} style={{ flex: 1 }}>
                      {uploadMessage}
                    </Box>
                    <AppButton
                      label={labelUploadBtn}
                      mode="outlined"
                      rounder
                      style={{ fontSize: 11, textTransform: "none", height: 28 }}
                      onClick={() => {
                        if (hiddenFileInputRef.current) {
                          hiddenFileInputRef.current.value = null;
                          hiddenFileInputRef.current.click();
                        }
                      }}
                    />
                  </Box>

                  {isShowListFile && (items.length > 0 || localFiles.length > 0) && (
                    <Box style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {/* Block 1: New local files */}
                      {localFiles.length > 0 && (
                        <Box>
                          <Typography variant="caption" style={{ fontWeight: 700, color: '#059669', marginBottom: 4, display: 'block', textTransform: 'uppercase', fontSize: 11 }}>
                            {t("label.newVersion")}
                          </Typography>
                          {localFiles.map((file, index) => (
                            <Box key={`local-${index}`} style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '6px 10px', marginBottom: '4px',
                              backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '6px',
                            }}>
                              <i className="fas fa-file" style={{ fontSize: 14, color: "#6B7280" }} />
                              <span style={{ fontSize: 12, color: "#374151", flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {file.name || 'Unknown file'}
                              </span>
                              <i
                                className="fas fa-trash"
                                style={{ fontSize: 14, color: "#EF4444", cursor: 'pointer' }}
                                onClick={() => handleRemoveLocalFile(index)}
                              />
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Block 2: Current server file (items[0]) - cannot be deleted (no delete icon) */}
                      {items.length > 0 && (() => {
                        const currentItem = items[0];
                        const serverFileName = currentItem[fileNameKey]
                          || currentItem.fileName
                          || currentItem.generalName
                          || currentItem[fileLinkKey]?.split('/').pop()
                          || 'Unknown file';

                        return (
                          <Box key="server-current">
                            <Typography variant="caption" style={{ fontWeight: 700, color: '#0D9488', marginBottom: 4, display: 'block', textTransform: 'uppercase', fontSize: 11 }}>
                              {t("label.currentVersion")}
                            </Typography>
                            <Box style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '6px 10px',
                              backgroundColor: '#F0FDFA', border: '1px solid #CCFBF1', borderRadius: '6px',
                            }}>
                              <i className="fas fa-file" style={{ fontSize: 14, color: "#6B7280" }} />
                              <span style={{
                                fontSize: 12,
                                color: "#374151",
                                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {serverFileName}
                              </span>
                            </Box>
                          </Box>
                        );
                      })()}

                      {/* Block 3: Past server files (items.slice(1)) */}
                      {items.length > 1 && (
                        <Box>
                          <Typography variant="caption" style={{ fontWeight: 700, color: '#6B7280', marginBottom: 4, display: 'block', textTransform: 'uppercase', fontSize: 11 }}>
                            {t("label.pastVersions")}
                          </Typography>
                          {items.slice(1).map((serverItem, index) => {
                            const serverFileName = serverItem[fileNameKey]
                              || serverItem.fileName
                              || serverItem.generalName
                              || serverItem[fileLinkKey]?.split('/').pop()
                              || 'Unknown file';
                            const isDeleted = deletedIds.includes(serverItem.id);

                            return (
                              <Box key={`server-past-${index}`} style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '6px 10px', marginBottom: '4px',
                                backgroundColor: isDeleted ? '#FEE2E2' : '#F9FAFB',
                                border: `1px solid ${isDeleted ? '#FCA5A5' : '#E5E7EB'}`,
                                borderRadius: '6px',
                                opacity: isDeleted ? 0.6 : 1,
                              }}>
                                <i className="fas fa-file" style={{ fontSize: 14, color: isDeleted ? "#991B1B" : "#6B7280" }} />
                                <span style={{
                                  fontSize: 12,
                                  color: isDeleted ? "#991B1B" : "#374151",
                                  flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  textDecoration: isDeleted ? 'line-through' : 'none',
                                }}>
                                  {serverFileName}
                                </span>
                                {isDeleted ? (
                                  <Tooltip title={t('button.restore', 'Restore')}>
                                    <i
                                      className="fas fa-undo"
                                      style={{ fontSize: 14, color: "#059669", cursor: 'pointer' }}
                                      onClick={() => handleUndeleteServerFile(serverItem.id)}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Tooltip title={t('button.delete', 'Delete')}>
                                    <i
                                      className="fas fa-trash"
                                      style={{ fontSize: 14, color: "#EF4444", cursor: 'pointer' }}
                                      onClick={() => handleDeleteServerFile(serverItem.id)}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}

                  <input
                    accept="image/jpeg,image/png,image/jpg,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    style={{ display: 'none' }}
                    id="upload-modal-file-input"
                    type="file"
                    multiple={isMultiple}
                    ref={hiddenFileInputRef}
                    onChange={handleFileChange}
                  />
                </Box>

                <Typography className={classes.sectionTitle}>
                  {t('clientDocument.sectionDetails')}
                </Typography>

                <div className="row">
                  {categories.length > 0 ? (
                    <div className="col-sm-12 col-md-6">
                      <Field
                        component={SelectField}
                        name="fileType"
                        label={t('label.fileType')}
                        options={categories.map(c => ({ value: c.key, label: c.label }))}
                        placeholder={t('label.selectFileType')}
                      />
                    </div>
                  ) : (
                    <div className="col-sm-12 col-md-12 mb-3">
                      <label style={{ paddingTop: 0 }}>{t('label.fileType')}</label>
                      <Typography variant="body2">{fileTypeLabel}</Typography>
                    </div>
                  )}

                  {showExpiry && (
                    <div className="col-sm-12 col-md-6">
                      <Field
                        component={CustomDatePicker}
                        label={t('label.expiryDate')}
                        name="expiryDate"
                        type="text"
                        id="modalExpiryDate"
                        formatDateJob={true}
                        required
                        classInput={'date-picker-container-custom'}
                      />
                    </div>
                  )}
                </div>

                {computedShowStatus && (
                  <>
                    <div className="row">
                      <div className="col-sm-12 col-md-6">
                        <Field
                          component={SelectField}
                          name="status"
                          label={t('label.status')}
                          options={[
                            { value: FILE_STATUS.PENDING, label: t('fileStatus.PENDING') },
                            { value: FILE_STATUS.VERIFIED, label: t('fileStatus.VERIFIED') },
                          ]}
                          placeholder={t('label.selectStatus')}
                        />
                      </div>
                    </div>
                  </>
                )}

                <Box className={classes.control}>
                  <AppButton
                    label={t("button.cancel")}
                    onClick={onClose}
                    buttonColor="#F3F4F6"
                    rounder
                    style={{ color: "#111827" }}
                  />
                  <AppButton
                    label={t("button.confirm")}
                    rounder
                    type="submit"
                    disabled={(localFiles.length === 0 && !initialExpiry && !initialStatus) || (categories.length > 0 && !values.fileType)}
                    buttonColor={AppColor.mainColor}
                  />
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </AppDialog>
  );
};

export default StaffUploadDocumentModal;
